import ConsumptionReport from './ConsumptionReport.js';

export default class CurrentConsumptionReport extends ConsumptionReport {
  /**
   * List of non-summary voice buckets.
   *
   * @return {Bucket[]}
   */
  get voiceDetailBuckets() {
    return this._buckets.filter(bucket => bucket.isVoice && bucket.isDetail && !bucket.isEmpty);
  }

  /**
   * List of non-summary data buckets.
   *
   * @return {Bucket[]}
   */
  get dataDetailBuckets() {
    return this._buckets.filter(bucket => bucket.isData && bucket.isDetail && !bucket.isEmpty);
  }

  /**
   * List of non-summary message buckets.
   *
   * @return {Bucket[]}
   */
  get messageDetailBuckets() {
    return this._buckets.filter(bucket => bucket.isMessage && bucket.isDetail && !bucket.isEmpty);
  }

  /**
   * List of non-summary monetary buckets. These only represent credit packages in combined post-paid
   * tariffs with credit such as "GO Safe Mini". Overconsumption is not associated with these, instead
   * overconsumption would be accounted for voice/data/messages separately.
   *
   * @return {Bucket[]}
   */
  get monetaryDetailBuckets() {
    return this._buckets.filter(bucket => bucket.isMonetary && !bucket.isSummary);
  }

  /**
   * List of buckets related to data sharing services.
   *
   * @return {Bucket[]}
   */
  get dataSharingBuckets() {
    return this._buckets.filter(bucket => bucket.isDataSharing || bucket.isDataSharingBonus);
  }

  /**
   * @return {?Bucket}
   */
  get voiceSummaryBucket() {
    return this.getSingleBucketByPredicate(bucket => bucket.isSummary && bucket.isVoice);
  }

  /**
   * @return {?Bucket}
   */
  get dataSummaryBucket() {
    return this.getSingleBucketByPredicate(bucket => bucket.isSummary && bucket.isData);
  }

  /**
   * @return {?Bucket}
   */
  get messageSummaryBucket() {
    return this.getSingleBucketByPredicate(bucket => bucket.isSummary && bucket.isMessage);
  }

  /**
   * @return {?Bucket}
   */
  get monetarySummaryBucket() {
    return this.getSingleBucketByPredicate(bucket => bucket.isSummary && bucket.isMonetary);
  }

  /**
   * @return {?Bucket}
   */
  get dataCapBucket() {
    return this.getSingleBucketByPredicate(bucket => bucket.isDataCap);
  }

  /**
   * @return {?Bucket}
   */
  get goCreditBucket() {
    return this.getSingleBucketByPredicate(bucket => bucket.isGoCredit);
  }

  /**
   * @return {?Bucket}
   */
  get creditBucket() {
    return this.getSingleBucketByPredicate(bucket => bucket.isCredit);
  }

  /**
   * @return {?Bucket}
   */
  get bonusCreditBucket() {
    return this.getSingleBucketByPredicate(bucket => bucket.isBonusCredit);
  }

  /**
   * Add up monetary overconsumption value of all three summary buckets.
   *
   * @return {number}
   */
  get totalOverconsumption() {
    const summaryBuckets = [
      this.voiceSummaryBucket,
      this.dataSummaryBucket,
      this.messageSummaryBucket,
    ];

    return summaryBuckets
      .map(bucket => {
        if (!bucket?.overconsumptionMonetary) {
          return 0; // TODO: confirm that the element is optional
        }
        return bucket.overconsumptionMonetary;
      })
      .reduce((total, current) => total + current, 0);
  }

  /**
   * Method for discovering any potential issues with given response. Returns a string with a list of all found issues
   * or null if none are found.
   *
   * @returns {?string}
   */
  get issuesLog() {
    const errors = [];
    const debugBucket = bucket => {
      if (!bucket) {
        return;
      }
      const bucketDebuggers = [
        () => {
          // eslint-disable-next-line no-unused-expressions
          bucket.type;
        },
        () => {
          if (bucket.isCredit || bucket.isBonusCredit) {
            return;
          }
          // eslint-disable-next-line no-unused-expressions
          bucket.total;
        },
        () => {
          if (bucket.isCredit || bucket.isBonusCredit) {
            return;
          }
          // eslint-disable-next-line no-unused-expressions
          bucket.used;
        },
        () => {
          // eslint-disable-next-line no-unused-expressions
          bucket.remaining;
        },
        () => {
          if (bucket.isCredit || bucket.isBonusCredit) {
            return;
          }
          // eslint-disable-next-line no-unused-expressions
          bucket.carryOver;
        },
        () => {
          if (bucket.isCredit || bucket.isBonusCredit) {
            return;
          }
          // avoid logging error in these getters twice
          const [used, remaining, total] = (() => {
            try {
              return [bucket.used, bucket.remaining, bucket.total];
            } catch (error) {
              return [0, 0, 0];
            }
          })();

          // Used + Remaining === Total +-0.001 -> leeway is left due to JS rounding errors
          if (!bucket.isInfinite && Math.abs(total - used - remaining) >= 0.001) {
            throw new Error(
              `Bucket "${bucket.id}" values don't add up. Used ${bucket.used} + Remaining ${bucket.remaining} != Total ${bucket.total}`,
            );
          }
        },
        () => {
          // eslint-disable-next-line no-unused-expressions
          bucket.name;
        },
        () => {
          // eslint-disable-next-line no-unused-expressions
          bucket.overconsumption;
        },
        () => {
          // eslint-disable-next-line no-unused-expressions
          bucket.overconsumptionMonetary;
        },
      ];

      bucketDebuggers.forEach(deb => {
        try {
          deb();
        } catch (error) {
          errors.push(error);
        }
      });
    };

    const debuggers = [
      () => {
        this.voiceDetailBuckets.forEach(debugBucket);
      },
      () => {
        this.dataDetailBuckets.forEach(debugBucket);
      },
      () => {
        this.messageDetailBuckets.forEach(debugBucket);
      },
      () => {
        this.monetaryDetailBuckets.forEach(debugBucket);
      },
      () => {
        debugBucket(this.voiceSummaryBucket);
      },
      () => {
        debugBucket(this.dataSummaryBucket);
      },
      () => {
        debugBucket(this.messageSummaryBucket);
      },
      () => {
        debugBucket(this.monetarySummaryBucket);
      },
      () => {
        debugBucket(this.dataCapBucket);
      },
      () => {
        // eslint-disable-next-line no-unused-expressions
        this.effectiveDate;
      },
      () => {
        // eslint-disable-next-line no-unused-expressions
        this.effectiveDateForData;
      },
      () => {
        // eslint-disable-next-line no-unused-expressions
        this.totalOverconsumption;
      },
      () => {
        // eslint-disable-next-line no-unused-expressions
        this.showRemaining;
      },
    ];

    debuggers.forEach(deb => {
      try {
        deb();
      } catch (error) {
        errors.push(error);
      }
    });

    if (errors.length === 0) {
      return null;
    }

    let message = `Current consumption issues for SN: ${this.sn}\n`;
    errors.forEach(error => {
      message += `${error}\n`;
    });

    return message;
  }

  /**
   * Returns overview of all available buckets for logging purposes.
   *
   * @returns {string}
   */
  get bucketLog() {
    if (this._buckets.length === 0) {
      return `No buckets for SN "${this.sn}".`;
    }
    let message = `Current consumption buckets for SN "${this.sn}":\n`;
    this._buckets.forEach(bucket => {
      try {
        if (bucket.isCredit || bucket.isBonusCredit) {
          message += `${bucket.id} (credit) - ${bucket.name}: remaining ${bucket.remaining}\n`;
        } else {
          message += `${bucket.id}${bucket.isSummary ? ' (summary)' : ''} - ${bucket.name}: used ${
            bucket.used
          } / total ${bucket.total}; remaining ${bucket.remaining}\n`;
        }
      } catch (error) {
        message += `${bucket.id} - Bucket is broken.\n`;
      }
    });

    return message;
  }
}
